import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import {
  signIn,
  signOut,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth'
import { setEffectiveOrgId } from '@/services/api'
import type { Role } from '@/types'

/**
 * Authenticated user with claims from JWT token.
 * Mirrors the backend auth.User struct.
 */
export interface AuthUser {
  id: string
  orgId: string
  role: Role
  email: string
  name: string
}

/**
 * Result of a `login` call. `signed_in` means the user is fully authenticated
 * and `user` will be populated. `new_password_required` means Cognito
 * accepted the temporary password but requires a new one (typical on the
 * first sign-in for admin-invited users) — callers must follow up with
 * `completeNewPassword` before the user is signed in. `totp_required` means
 * the user has TOTP MFA enabled and must submit a 6-digit code via
 * `confirmTotpCode`. `totp_setup_required` means Cognito is forcing TOTP
 * setup on this sign-in attempt (only fires when a pool-level MFA policy
 * requires TOTP before sign-in is allowed); callers must walk the user
 * through an authenticator-app enrollment and submit the verification code
 * via `confirmTotpCode`.
 */
export type LoginResult =
  | 'signed_in'
  | 'new_password_required'
  | 'totp_required'
  | 'totp_setup_required'

/**
 * TOTP setup payload returned when Cognito requires the user to enroll a
 * TOTP authenticator before sign-in completes. `setupUri` is the
 * otpauth:// URL that authenticator apps scan; `sharedSecret` is the raw
 * Base32 secret for users entering it by hand.
 */
export interface TotpSetupDetails {
  setupUri: string
  sharedSecret: string
}

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  /**
   * TOTP setup payload, populated when `login` returns
   * `'totp_setup_required'` and cleared on sign-in completion / logout.
   */
  totpSetupDetails: TotpSetupDetails | null
  login: (email: string, password: string) => Promise<LoginResult>
  completeNewPassword: (newPassword: string) => Promise<void>
  confirmTotpCode: (code: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Check if running in local auth mode (no Cognito).
 */
const isLocalAuthMode = import.meta.env.VITE_AUTH_MODE === 'local'

/**
 * Predefined local dev users, keyed by token.
 * Must match the backend's api/internal/auth/local.go predefinedUsers.
 */
const LOCAL_MOCK_USERS: Record<string, AuthUser> = {
  'super-admin-token': {
    id: 'super-admin-1',
    orgId: '',
    role: 'super_admin',
    email: 'superadmin@test.local',
    name: 'Super Admin',
  },
  'admin-token': {
    id: 'admin-1',
    orgId: '8f4a9b4c-9c57-4b7e-8e2e-3c1a7f5a9d21',
    role: 'admin',
    email: 'admin@test.local',
    name: 'Test Admin',
  },
  'employee-token': {
    id: 'employee-1',
    orgId: '8f4a9b4c-9c57-4b7e-8e2e-3c1a7f5a9d21',
    role: 'employee',
    email: 'employee@test.local',
    name: 'Test Employee',
  },
  'viewer-token': {
    id: 'viewer-1',
    orgId: '8f4a9b4c-9c57-4b7e-8e2e-3c1a7f5a9d21',
    role: 'viewer',
    email: 'viewer@test.local',
    name: 'Test Viewer',
  },
  'org2-admin-token': {
    id: 'org2-admin-1',
    orgId: 'b5e3d2a1-7f86-4c3a-9e1d-2a8b6c4f7e35',
    role: 'admin',
    email: 'admin@org2.local',
    name: 'Org2 Admin',
  },
}

/** Map shorthand names (e.g. "employee") to full token names (e.g. "employee-token"). */
const TOKEN_ALIASES: Record<string, string> = {
  'super-admin': 'super-admin-token',
  'super_admin': 'super-admin-token',
  'admin': 'admin-token',
  'employee': 'employee-token',
  'viewer': 'viewer-token',
  'org2-admin': 'org2-admin-token',
}

/** Resolve the configured token, supporting both full tokens and shorthand aliases. */
function resolveLocalToken(): string {
  const raw = import.meta.env.VITE_AUTH_TOKEN || 'admin-token'
  return TOKEN_ALIASES[raw] ?? raw
}

const LOCAL_AUTH_TOKEN = resolveLocalToken()

function getLocalMockUser(): AuthUser | null {
  const user = LOCAL_MOCK_USERS[LOCAL_AUTH_TOKEN]
  if (!user) {
    console.error(
      `[AuthContext] Unknown VITE_AUTH_TOKEN="${import.meta.env.VITE_AUTH_TOKEN}". ` +
      `Valid tokens: ${Object.keys(LOCAL_MOCK_USERS).join(', ')}`
    )
    return null
  }
  return user
}

/**
 * Parse JWT claims from Cognito token to extract user information.
 */
function parseTokenClaims(idToken: string): AuthUser {
  // Decode the JWT payload (middle part between dots)
  const payload = idToken.split('.')[1]
  const decoded = JSON.parse(atob(payload))

  return {
    id: decoded.sub,
    orgId: decoded['custom:orgId'] || '',
    role: decoded['custom:role'] || 'viewer',
    email: decoded.email || '',
    name: decoded.name || decoded.email || '',
  }
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [totpSetupDetails, setTotpSetupDetails] = useState<TotpSetupDetails | null>(null)

  /**
   * Load the current user session on mount.
   */
  const loadUser = useCallback(async () => {
    // In local mode, use mock user immediately
    if (isLocalAuthMode) {
      setUser(getLocalMockUser())
      setIsLoading(false)
      return
    }

    try {
      // Check if user is signed in
      await getCurrentUser()

      // Fetch the auth session to get tokens
      const session = await fetchAuthSession()
      const idToken = session.tokens?.idToken?.toString()

      if (idToken) {
        const authUser = parseTokenClaims(idToken)
        setUser(authUser)
      } else {
        setUser(null)
      }
    } catch {
      // User is not authenticated
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadUser()
  }, [loadUser])

  /**
   * Fetch the current Cognito session and hydrate local user state from the
   * ID token claims. Shared between `login` and `completeNewPassword` so
   * both paths produce the same end-state when authentication succeeds.
   */
  const hydrateUserFromSession = useCallback(async () => {
    const session = await fetchAuthSession()
    const idToken = session.tokens?.idToken?.toString()
    if (idToken) {
      setUser(parseTokenClaims(idToken))
    }
  }, [])

  /**
   * Sign in with email and password.
   *
   * Returns `'signed_in'` on the happy path. When Cognito requires a new
   * password (admin-created users on first login), returns
   * `'new_password_required'` — callers should route the user to a new
   * password form and call `completeNewPassword` with the chosen password.
   * When Cognito requires a TOTP code (user has MFA enabled), returns
   * `'totp_required'` — callers should prompt for the 6-digit code and
   * call `confirmTotpCode`. When Cognito requires TOTP enrollment before
   * sign-in completes, returns `'totp_setup_required'` and populates
   * `totpSetupDetails` with the otpauth URI + shared secret; after the
   * user scans the QR code in their authenticator, call `confirmTotpCode`
   * with the verification code.
   * Any other `signInStep` is treated as unsupported and thrown so the
   * login-page error handler can surface a generic message.
   */
  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    // In local mode, just set the mock user
    if (isLocalAuthMode) {
      setUser(getLocalMockUser())
      return 'signed_in'
    }

    setTotpSetupDetails(null)

    const result = await signIn({
      username: email,
      password,
    })

    if (result.isSignedIn) {
      await hydrateUserFromSession()
      return 'signed_in'
    }

    if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return 'new_password_required'
    }

    if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      return 'totp_required'
    }

    if (result.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
      const details = result.nextStep.totpSetupDetails
      setTotpSetupDetails({
        setupUri: details.getSetupUri('Tap-Tidy', email).toString(),
        sharedSecret: details.sharedSecret,
      })
      return 'totp_setup_required'
    }

    // Any other step (SMS MFA, MFA selection, custom challenge, etc.)
    // isn't wired up — surface a throwable so LoginPage's error handler
    // can display a friendly fallback rather than leaving the user stuck.
    throw new Error(
      `Sign-in requires additional steps: ${result.nextStep.signInStep}`
    )
  }, [hydrateUserFromSession])

  /**
   * Complete the NEW_PASSWORD_REQUIRED challenge with the user's chosen
   * password. Must be called after `login` returned `'new_password_required'`
   * within the same Amplify session — Amplify tracks the in-flight
   * challenge internally so no handle is passed here.
   *
   * Throws if Cognito rejects the password (policy violation, expired
   * session, etc.) so the caller can surface the error. On success the
   * user is hydrated from the new session just like a normal sign-in.
   */
  const completeNewPassword = useCallback(async (newPassword: string) => {
    if (isLocalAuthMode) {
      // Local auth skips Cognito entirely, so the new-password challenge
      // is never issued. Reaching this branch means the caller confused
      // the two modes — fail loudly instead of silently drifting.
      throw new Error('completeNewPassword is not supported in local auth mode')
    }

    const result = await confirmSignIn({ challengeResponse: newPassword })
    if (!result.isSignedIn) {
      throw new Error(
        `Sign-in still requires additional steps: ${result.nextStep.signInStep}`
      )
    }
    await hydrateUserFromSession()
  }, [hydrateUserFromSession])

  /**
   * Submit a 6-digit TOTP code to complete the current sign-in challenge.
   * Used for both the `CONFIRM_SIGN_IN_WITH_TOTP_CODE` step (existing
   * enrolled users) and the follow-up verification of a fresh
   * `CONTINUE_SIGN_IN_WITH_TOTP_SETUP` enrollment — Amplify treats them
   * identically at this point.
   *
   * Throws if Cognito rejects the code (wrong code, expired session) so
   * the caller can surface the error. Clears `totpSetupDetails` on
   * success so the secret isn't retained in memory longer than needed.
   */
  const confirmTotpCode = useCallback(async (code: string) => {
    if (isLocalAuthMode) {
      throw new Error('confirmTotpCode is not supported in local auth mode')
    }

    const result = await confirmSignIn({ challengeResponse: code })
    if (!result.isSignedIn) {
      throw new Error(
        `Sign-in still requires additional steps: ${result.nextStep.signInStep}`
      )
    }
    setTotpSetupDetails(null)
    await hydrateUserFromSession()
  }, [hydrateUserFromSession])

  /**
   * Sign out the current user.
   */
  const logout = useCallback(async () => {
    // Clear any super_admin org selection so a different user signing in on
    // this device does not inherit the prior selection.
    setEffectiveOrgId('')
    setTotpSetupDetails(null)

    if (isLocalAuthMode) {
      setUser(null)
      return
    }

    await signOut()
    setUser(null)
  }, [])

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    totpSetupDetails,
    login,
    completeNewPassword,
    confirmTotpCode,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to access the auth context.
 * Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
